import {
  Activity,
  AlertTriangle,
  Pause,
  Sparkles,
  UserPlus,
  UserX,
} from "lucide-react";
import { useSegmentCounts } from "../hooks/use-broadcast";
import { BroadcastFilters, LifecycleSegment } from "../services/broadcast";

interface SegmentMeta {
  key: LifecycleSegment;
  label: string;
  description: string;
  icon: React.ElementType;
}

// Order mirrors the lifecycle journey.
const SEGMENTS: SegmentMeta[] = [
  {
    key: "LEAD",
    label: "Lead",
    description: "Signed up — no session or payment",
    icon: UserPlus,
  },
  {
    key: "NEW",
    label: "New",
    description: "First session in the last 30 days",
    icon: Sparkles,
  },
  {
    key: "ACTIVE",
    label: "Active",
    description: "Session in last 30d + active bundle",
    icon: Activity,
  },
  {
    key: "INACTIVE",
    label: "Inactive",
    description: "Has a bundle, no recent session",
    icon: Pause,
  },
  {
    key: "AT_RISK",
    label: "At-risk",
    description: "31–90d ago, no bundle, nothing booked",
    icon: AlertTriangle,
  },
  {
    key: "CHURNED",
    label: "Churned",
    description: "No session in 90+ days",
    icon: UserX,
  },
];

interface Props {
  selected: LifecycleSegment[];
  onChange: (next: LifecycleSegment[]) => void;
  /** Audience filters (minus lifecycle) to scope the per-segment counts. */
  countsFilters: BroadcastFilters;
}

export function LifecycleTiles({ selected, onChange, countsFilters }: Props) {
  const { data, isFetching } = useSegmentCounts(countsFilters);
  const counts = data?.counts;

  const toggle = (key: LifecycleSegment) =>
    onChange(
      selected.includes(key)
        ? selected.filter((s) => s !== key)
        : [...selected, key],
    );

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {SEGMENTS.map((seg) => {
        const active = selected.includes(seg.key);
        const count = counts?.[seg.key];
        const Icon = seg.icon;
        return (
          <button
            key={seg.key}
            type="button"
            onClick={() => toggle(seg.key)}
            aria-pressed={active}
            className={`flex flex-col items-start rounded-xl border p-4 text-left transition ${
              active
                ? "border-orange-500 bg-orange-50 ring-1 ring-orange-500"
                : "border-gray-200 bg-white hover:border-gray-300"
            }`}
          >
            <div className="flex w-full items-center justify-between">
              <Icon
                className={`h-5 w-5 ${active ? "text-orange-600" : "text-gray-400"}`}
              />
              <span
                className={`font-display text-lg font-semibold ${
                  active ? "text-orange-700" : "text-gray-900"
                }`}
              >
                {count === undefined
                  ? isFetching
                    ? "…"
                    : "—"
                  : count.toLocaleString()}
              </span>
            </div>
            <span className="mt-2 text-sm font-medium text-gray-800">
              {seg.label}
            </span>
            <span className="mt-0.5 text-xs text-gray-500">
              {seg.description}
            </span>
          </button>
        );
      })}
    </div>
  );
}
